import { UserPlus } from 'lucide-react'
import { useState } from 'react'

import type { User } from '@/gen/customer/management/v1/service_pb'

import { cn } from '@/lib/ui/style-helpers'

import { AvatarList } from '@/components/lemmala-design-system/avatar'
import { Button } from '@/components/lemmala-design-system/button/button'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { AssigneeDropdown } from '@/components/shared/assignee/assignee-dropdown'
import { assigneeLabels } from '@/components/shared/assignee/labels'

type AssigneeDisplayProps = {
  assignees: User[]
  triggerClassName?: string
  // Show names next to avatars (details pane) instead of avatars-only (table cells).
  withNames?: boolean
} & (
  | { readOnly: true }
  | {
      readOnly?: false
      onAssign: (userId: string) => Promise<unknown>
      onUnassign: (userId: string) => Promise<unknown>
      popoverAlign?: 'start' | 'center' | 'end'
      onAddClick?: () => void
    }
)

const MAX_ASSIGNEES_TO_DISPLAY = 3

export const AssigneeDisplay = (props: AssigneeDisplayProps) => {
  const { assignees, triggerClassName, withNames } = props
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const assigneeNames = assignees.map((assignee) => assignee.name || assignee.email)

  const AddAssigneesTooltipTrigger = (
    <span className='flex h-6 min-w-6 items-center justify-center'>
      <UserPlus className='size-4' />
    </span>
  )
  const AssigneeListTooltipTrigger = (
    <span className={cn('flex h-6 items-center', withNames ? 'gap-2' : 'min-w-6 justify-center')}>
      <AvatarList labels={assigneeNames} size='small' maxVisible={MAX_ASSIGNEES_TO_DISPLAY} />
      {withNames && assigneeNames.length > 0 && (
        <span className='truncate text-main-foreground-primary text-body-default'>
          {assigneeNames[0]}
          {assigneeNames.length > 1 && (
            <span className='text-main-foreground-secondary'> +{assigneeNames.length - 1}</span>
          )}
        </span>
      )}
    </span>
  )

  if (props.readOnly) {
    if (assignees.length === 0) return null

    // Same box size as the editable ghost-button trigger, so both kinds of row line up.
    return (
      <span className={cn('inline-flex h-8 items-center px-3', triggerClassName)}>
        {AssigneeListTooltipTrigger}
      </span>
    )
  }

  const { onAssign, onUnassign, popoverAlign, onAddClick } = props

  return (
    <Tooltip
      tooltipDisabled={dropdownOpen || assignees.length > 0}
      asChild={true}
      trigger={
        <span className='inline-flex'>
          <AssigneeDropdown
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            selectedUsers={assignees}
            onAssign={onAssign}
            onUnassign={onUnassign}
            trigger={
              <Button
                variant='ghost'
                className={cn('flex items-center justify-center', triggerClassName)}
                size='default'
                onClick={onAddClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                  }
                }}
              >
                {assignees.length === 0 ? AddAssigneesTooltipTrigger : AssigneeListTooltipTrigger}
              </Button>
            }
            popoverAlign={popoverAlign}
          />
        </span>
      }
    >
      <p>{assigneeLabels.addAssignees}</p>
    </Tooltip>
  )
}
